import { useState, useCallback } from "react";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { UPDATE_MENU_ITEM } from "src/constants/mutations";
import { UpdateItemInput } from "src/interfaces/menu.interface";
import { MutationCallbacks } from "src/interfaces/menu-items-props.interface";
import { UUID } from "src/types/shared.type";

export const useUpdateMenuItem = () => {
  const { axBe } = useAxiosInterceptor();
  const [isUpdatingMenuItem, setIsUpdatingMenuItem] = useState(false);

  const updateMenuItemMutation = useCallback(
    async (
      updatedMenuItemInput: UpdateItemInput,
      callbacks?: MutationCallbacks
    ) => {
      setIsUpdatingMenuItem(true);
      try {
        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: UPDATE_MENU_ITEM,
          variables: {
            updateItemInput: updatedMenuItemInput,
          },
        });

        if (data?.errors?.length) {
          throw data?.errors[0];
        }

        await Promise.resolve(callbacks?.onSuccess?.(data?.data?.updateItem));
      } catch (err) {
        console.error("Error updating menu item:", err);
        callbacks?.onError?.(err);
      } finally {
        setIsUpdatingMenuItem(false);
      }
    },
    [axBe]
  );

  const updateMenuItemFromFormValues = useCallback(
    async (
      formValues: UpdateItemInput,
      menuItemCorrelationId: UUID | undefined,
      callbacks?: MutationCallbacks
    ) => {
      const {
        englishDescription,
        englishName,
        nonEnglishDescription,
        nonEnglishName,
        priceCents,
        abbreviation,
        taxRate,
        itemTagCorrelationIdList,
        storePrinterCorrelationId,
        isActive,
        isQuickServiceChoiceItem,
        enterpriseEnabled,
        translations,
      } = formValues || {};

      // Ensure itemCorrelationId is a valid UUID (not empty string)
      if (!menuItemCorrelationId || menuItemCorrelationId.trim() === "") {
        throw new Error("menuItemCorrelationId is required and must be a valid UUID");
      }

      // Filter out empty strings from itemTagCorrelationIdList (must be valid UUIDs)
      // Use empty array [] instead of undefined to explicitly clear tags when user selects "None"
      const filteredItemTagCorrelationIdList = itemTagCorrelationIdList && itemTagCorrelationIdList.length > 0
        ? itemTagCorrelationIdList.filter((tagId: string) => tagId && tagId.trim() !== "")
        : [];

      const updatedItemInput: UpdateItemInput = {
        itemCorrelationId: menuItemCorrelationId,
        englishName,
        englishDescription,
        priceCents,
        abbreviation,
        taxRate,
        nonEnglishDescription,
        nonEnglishName,
        itemTagCorrelationIdList: filteredItemTagCorrelationIdList,
        storePrinterCorrelationId: storePrinterCorrelationId === "" || storePrinterCorrelationId === null 
          ? undefined 
          : storePrinterCorrelationId,
        isActive,
        isQuickServiceChoiceItem,
        enterpriseEnabled,
        translations,
      };

      return updateMenuItemMutation(updatedItemInput, callbacks);
    },
    [updateMenuItemMutation]
  );

  return {
    updateMenuItemMutation,
    updateMenuItemFromFormValues,
    isUpdatingMenuItem,
  };
};
